// import React from "react";
// import type { TicketMessage } from "../../types/supportTicket";
// import { formatDateTime } from "../../utils/dateFormat";

// interface Props {
//   messages: TicketMessage[];
// }

// const imageUrl = import.meta.env.VITE_LYRAZONE_IMAGE_URL;

// const TicketConversation: React.FC<Props> = ({ messages }) => {
//   return (
//     <div className="ticket-conversation">
//       {messages.length > 0 ? (
//         messages.map((msg) => (
//           <div
//             key={msg.id}
//             className={`ticket-message-card ${msg.senderType === "admin" ? "admin" : "subscriber"}`}
//           >
//             <div className="ticket-message-head">
//               <strong>{msg.senderName}</strong>
//               <span>{formatDateTime(msg.createdAt)}</span>
//             </div>

//             <div className="ticket-message-body">{msg.message}</div>

//             {msg.attachments && msg.attachments.length > 0 && (
//               <div className="ticket-message-files">
//                 {msg.attachments.map((file, index) => (
//                   <a
//                     key={index}
//                     href={`${imageUrl}${file.fileUrl}`}
//                     target="_blank"
//                     rel="noreferrer"
//                   >
//                     {file.fileName}
//                   </a>
//                 ))}
//               </div>
//             )}
//           </div>
//         ))
//       ) : (
//         <div className="ticket-empty-box">No conversation yet.</div>
//       )}
//     </div>
//   );
// };

// export default TicketConversation;

import React from "react";
import type { TicketMessage } from "../../types/supportTicket";
import { formatDateTime } from "../../utils/dateFormat";

interface Props {
  messages: TicketMessage[];
}

interface TicketAttachment {
  fileName?: string;
  fileUrl: string;
  mimeType?: string;
  fileType?: string;
}

const imageUrl = import.meta.env.VITE_LYRAZONE_IMAGE_URL;

const getAttachmentUrl = (fileUrl: string) => {
  if (!fileUrl) return "";

  if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
    return fileUrl;
  }

  const base = String(imageUrl || "").replace(/\/$/, "");
  const path = fileUrl.startsWith("/") ? fileUrl : `/${fileUrl}`;

  return `${base}${path}`;
};

const getFileExtension = (file: TicketAttachment) => {
  const source = file.fileName || file.fileUrl || "";
  const cleanSource = source.split("?")[0];
  const parts = cleanSource.split(".");

  return parts.length > 1 ? parts.pop()?.toLowerCase() || "" : "";
};

const isImageFile = (file: TicketAttachment) => {
  const ext = getFileExtension(file);
  const mime = String(file.mimeType || file.fileType || "").toLowerCase();

  return (
    mime.startsWith("image/") ||
    ["jpg", "jpeg", "png", "webp", "gif", "svg", "bmp"].includes(ext)
  );
};

const isVideoFile = (file: TicketAttachment) => {
  const ext = getFileExtension(file);
  const mime = String(file.mimeType || file.fileType || "").toLowerCase();

  return (
    mime.startsWith("video/") ||
    ["mp4", "webm", "ogg", "mov", "mkv"].includes(ext)
  );
};

const isPdfFile = (file: TicketAttachment) => {
  const ext = getFileExtension(file);
  const mime = String(file.mimeType || file.fileType || "").toLowerCase();

  return mime.includes("pdf") || ext === "pdf";
};

const getFileBadge = (file: TicketAttachment) => {
  const ext = getFileExtension(file);

  if (isPdfFile(file)) return "PDF";
  if (["doc", "docx"].includes(ext)) return "DOC";
  if (["xls", "xlsx", "csv"].includes(ext)) return "XLS";
  if (["zip", "rar", "7z"].includes(ext)) return "ZIP";
  if (ext) return ext.toUpperCase();

  return "FILE";
};

const TicketConversation: React.FC<Props> = ({ messages }) => {
  const [previewFile, setPreviewFile] = React.useState<TicketAttachment | null>(
    null,
  );
  const renderAttachment = (file: TicketAttachment, index: number) => {
    const fileHref = getAttachmentUrl(file.fileUrl);
    const fileName = file.fileName || `Attachment ${index + 1}`;
    const canPreviewInModal = isImageFile(file) || isVideoFile(file);

    const handleAttachmentClick = (
      event: React.MouseEvent<HTMLAnchorElement>,
    ) => {
      if (canPreviewInModal) {
        event.preventDefault();
        setPreviewFile(file);
      }
    };

    return (
      <a
        key={`${file.fileUrl}-${index}`}
        href={fileHref}
        target="_blank"
        rel="noreferrer"
        className="ticket-attachment-card"
        title={fileName}
        onClick={handleAttachmentClick}
      >
        <div className="ticket-attachment-thumb">
          {isImageFile(file) ? (
            <img src={fileHref} alt={fileName} />
          ) : isVideoFile(file) ? (
            <video src={fileHref} muted preload="metadata" />
          ) : (
            <div className="ticket-file-placeholder">
              <span>{getFileBadge(file)}</span>
            </div>
          )}
        </div>

        <div className="ticket-attachment-info">
          <span className="ticket-attachment-name">{fileName}</span>
          <span className="ticket-attachment-view">
            {canPreviewInModal ? "Preview" : "View Attachment"}
          </span>
        </div>
      </a>
    );
  };

  return (
    <div className="ticket-conversation">
      {messages.length > 0 ? (
        messages.map((msg) => (
          <div
            key={msg.id}
            className={`ticket-message-card ${
              msg.senderType === "admin" ? "admin" : "subscriber"
            }`}
          >
            <div className="ticket-message-head">
              <strong>{msg.senderName}</strong>
              <span>{formatDateTime(msg.createdAt)}</span>
            </div>

            <div className="ticket-message-body">{msg.message}</div>

            {msg.attachments && msg.attachments.length > 0 && (
              <div className="ticket-message-files">
                {(msg.attachments as TicketAttachment[]).map(renderAttachment)}
              </div>
            )}
          </div>
        ))
      ) : (
        <div className="ticket-empty-box">No conversation yet.</div>
      )}

      {previewFile && (
        <div
          className="ticket-preview-modal-overlay"
          onClick={() => setPreviewFile(null)}
        >
          <div
            className="ticket-preview-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="ticket-preview-close"
              onClick={() => setPreviewFile(null)}
              aria-label="Close preview"
            >
              ×
            </button>

            <div className="ticket-preview-header">
              <span>{previewFile.fileName || "Attachment Preview"}</span>
            </div>

            <div className="ticket-preview-body">
              {isImageFile(previewFile) && (
                <img
                  src={getAttachmentUrl(previewFile.fileUrl)}
                  alt={previewFile.fileName || "Attachment"}
                  className="ticket-preview-image"
                />
              )}

              {isVideoFile(previewFile) && (
                <video
                  src={getAttachmentUrl(previewFile.fileUrl)}
                  className="ticket-preview-video"
                  controls
                  autoPlay
                />
              )}
            </div>

            <div className="ticket-preview-footer">
              <a
                href={getAttachmentUrl(previewFile.fileUrl)}
                target="_blank"
                rel="noreferrer"
                className="ticket-preview-open-new"
              >
                Open in New Tab
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketConversation;
